import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";
import { usePaddle, PaddleEvent } from "../components/PaddleProvider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@ensafely/checker-ui";

interface Plan {
  id: string;
  name: string;
  max_checks: number;
  max_users: number;
  max_alert_channels: number;
  price_monthly_cents: number;
  price_yearly_cents: number;
  addon_enabled: boolean;
  active: boolean;
  trial_days: number;
}

interface Subscription {
  id: string;
  plan_id: string;
  status: string;
  billing_cycle: string;
  current_period_end: string;
  cancel_at?: string | null;
  trial_end?: string | null;
  trial_start?: string | null;
}

interface AddonInfo {
  quantity: number;
  effective_limits: {
    max_checks: number;
    max_users: number;
    max_alert_channels: number;
  };
  cancel_at?: string | null;
}

type BillingCycle = "monthly" | "yearly";

function billingErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 501) return "Billing is not yet configured. Please contact support.";
    if (err.status === 400) return err.message || "Invalid request.";
    if (err.status === 422) return err.message || "Cannot complete this action due to current usage limits.";
  }
  return "Something went wrong. Please try again.";
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatLimit(val: number): string {
  return val < 0 ? "Unlimited" : String(val);
}

function trialDaysRemaining(trialEnd: string | null | undefined): number {
  if (!trialEnd) return 0;
  const end = new Date(trialEnd).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
}

interface ConfirmDialogProps {
  periodEnd: string;
  isTrialing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function CancelConfirmDialog({ periodEnd, isTrialing, onConfirm, onCancel, loading }: ConfirmDialogProps) {
  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{isTrialing ? "Cancel Trial" : "Cancel Subscription"}</DialogTitle>
          <DialogDescription>
            {isTrialing ? (
              <>
                Are you sure you want to cancel your trial? You won&apos;t be charged.
                Your access will continue until the trial ends
                {periodEnd ? <> (<strong>{formatDate(periodEnd)}</strong>)</> : ""}.
                After that, your account will be downgraded to the Free plan.
              </>
            ) : (
              <>
                Are you sure you want to cancel your subscription? Your access will continue
                until the end of your current billing period
                {periodEnd ? <> (<strong>{formatDate(periodEnd)}</strong>)</> : ""}.
                After that, your account will be downgraded to the Free plan.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            className="px-4 py-2 text-sm rounded border hover:bg-muted transition-colors"
            onClick={onCancel}
            disabled={loading}
          >
            {isTrialing ? "Keep Trial" : "Keep Subscription"}
          </button>
          <button
            className="px-4 py-2 text-sm rounded bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-50 transition-colors"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Cancelling..." : isTrialing ? "Cancel Trial" : "Yes, Cancel"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DowngradeWithAddonDialogProps {
  addonQty: number;
  freePlan: Plan;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function DowngradeWithAddonDialog({ addonQty, freePlan, onConfirm, onCancel, loading }: DowngradeWithAddonDialogProps) {
  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Downgrade to Free</DialogTitle>
          <DialogDescription asChild>
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                This will remove all <strong>{addonQty} add-on package{addonQty !== 1 ? "s" : ""}</strong>.
                Your limits will decrease to{" "}
                <strong>{formatLimit(freePlan.max_checks)} checks</strong>,{" "}
                <strong>{formatLimit(freePlan.max_users)} users</strong>, and{" "}
                <strong>{formatLimit(freePlan.max_alert_channels)} alert channels</strong>.
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Make sure your current usage is within the Free plan limits before downgrading.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            className="px-4 py-2 text-sm rounded border hover:bg-muted transition-colors"
            onClick={onCancel}
            disabled={loading}
          >
            Keep Plan
          </button>
          <button
            className="px-4 py-2 text-sm rounded bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-50 transition-colors"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Processing..." : "Downgrade to Free"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BillingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState("");
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [addon, setAddon] = useState<AddonInfo | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDowngrade, setConfirmDowngrade] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showAddonDowngradeDialog, setShowAddonDowngradeDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [activating, setActivating] = useState(false);

  const { paddle } = usePaddle();
  const [searchParams, setSearchParams] = useSearchParams();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Dedupe in-flight activations across the in-page (checkout.completed)
  // and post-redirect (?checkout=success + stashed txn) paths.
  const activeSyncTxnRef = useRef<string | null>(null);

  // Success URL Paddle overlay checkout redirects to after payment.
  // Using window.location.origin so it works on any deployed host.
  const checkoutSuccessUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/account/billing?checkout=success`
      : "/account/billing?checkout=success";

  // sessionStorage key used to survive the Paddle success redirect. Without
  // this, the transaction_id we receive on checkout.completed is lost when
  // Paddle navigates the page — and the sync call it would have made gets
  // cancelled mid-flight. Stashing it before opening checkout lets the
  // post-redirect handler pick it up and drive sync to completion.
  const PENDING_TXN_KEY = "paddle_pending_txn_id";
  const stashPendingTxn = (txnId: string) => {
    try {
      sessionStorage.setItem(PENDING_TXN_KEY, txnId);
    } catch {
      /* private mode / quota exceeded — sync will fall back to polling */
    }
  };
  const readPendingTxn = (): string | null => {
    try {
      return sessionStorage.getItem(PENDING_TXN_KEY);
    } catch {
      return null;
    }
  };
  const clearPendingTxn = () => {
    try {
      sessionStorage.removeItem(PENDING_TXN_KEY);
    } catch {
      /* ignore */
    }
  };

  // Strip Paddle / checkout query params from URL without adding a history entry.
  // Prevents refresh from re-triggering checkout (_ptxn) or re-showing success banner.
  const clearCheckoutParams = () => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    ["_ptxn", "checkout", "plan"].forEach((k) => {
      if (next.has(k)) {
        next.delete(k);
        changed = true;
      }
    });
    if (changed) setSearchParams(next, { replace: true });
  };

  const loadData = () => {
    api.get<{ plans: Plan[] }>("/billing/plans").then((r) => {
      // Filter out inactive plans; sort by price ascending (free first)
      const active = (r.plans || []).filter((p) => p.active);
      active.sort((a, b) => {
        const aFree = a.price_monthly_cents === 0 && a.price_yearly_cents === 0;
        const bFree = b.price_monthly_cents === 0 && b.price_yearly_cents === 0;
        if (aFree && !bFree) return -1;
        if (!aFree && bFree) return 1;
        return a.price_monthly_cents - b.price_monthly_cents;
      });
      setPlans(active);
    });
    api.get<{ plan_id: string }>("/tenants/current").then((r) => setCurrentPlan(r.plan_id));
    api
      .get<Subscription>("/billing/subscription")
      .then((sub) => setSubscription(sub))
      .catch(() => setSubscription(null));
    api
      .get<AddonInfo>("/billing/addon")
      .then((a) => setAddon(a))
      .catch(() => setAddon(null));
  };

  useEffect(() => {
    loadData();
  }, []);

  // Fire the Google Ads Purchase conversion once per subscription. Called
  // when a subscription first activates (either trial start or first real
  // payment).
  //
  // Gated on Google Ads attribution: we only fire if the _gcl_aw cookie
  // is present (or a gclid has ever been observed during this session).
  // That cookie is set by gtag.js when the visitor lands with a gclid=
  // URL parameter; gating on it prevents organic / direct signups from
  // inflating the Google Ads conversion count.
  //
  // Deduped per subscription id in localStorage so reloads or repeat
  // activations do not re-fire.
  const hasGoogleAdsAttribution = (): boolean => {
    try {
      if (document.cookie.split("; ").some((c) => c.startsWith("_gcl_aw="))) {
        return true;
      }
      // Fallback: if the user just landed with a gclid on this very page,
      // gtag may not have set the cookie yet by the time we check.
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("gclid")) return true;
      // Second fallback: persisted across the session (see attribution
      // capture in main.tsx) for the case where the gclid URL param was
      // stripped before we reached the billing page.
      if (sessionStorage.getItem("ensafely_gclid")) return true;
    } catch {
      /* cookie / storage access denied — treat as no attribution */
    }
    return false;
  };

  const fireConversion = (sub: Subscription) => {
    try {
      const key = `gads_conv_fired_${sub.id}`;
      if (localStorage.getItem(key)) return;
      if (!hasGoogleAdsAttribution()) return;
      // gtag is injected by the Google Ads tag at runtime, so it is not on the
      // Window type. no-explicit-any is not enabled in this config, hence no
      // suppression comment — adding one would just be reported as dead.
      const gtag = (window as any).gtag;
      if (typeof gtag !== "function") return;
      gtag("event", "conversion", {
        send_to: "AW-18093815560/nM4TCJbE3ZwcEIju5rND",
        value: 1.0,
        currency: "ILS",
        transaction_id: sub.id,
      });
      localStorage.setItem(key, String(Date.now()));
    } catch {
      /* gtag unavailable / localStorage blocked — ignore */
    }
  };

  // Finalize UI state once the subscription has landed in our DB.
  const finalizeActivation = async (sub: Subscription) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = null;
    activeSyncTxnRef.current = null;
    clearPendingTxn();
    setActivating(false);
    fireConversion(sub);
    try {
      const [tenant, addonData] = await Promise.all([
        api.get<{ plan_id: string }>("/tenants/current"),
        api.get<AddonInfo>("/billing/addon").catch(() => null),
      ]);
      setSubscription(sub);
      setCurrentPlan(tenant.plan_id);
      setAddon(addonData);
      setSuccess("Subscription activated! Your plan is now active.");
    } catch {
      // Still succeeded — UI will catch up on next load.
      setSubscription(sub);
      setSuccess("Subscription activated! Your plan is now active.");
    }
  };

  const ACTIVATE_INTERVAL = 3000;
  const ACTIVATE_TIMEOUT = 60000;
  const activationTimeoutMessage =
    "Payment was received but activation is taking longer than usual. " +
    "Refresh in a minute — if the plan still hasn't updated, contact support.";

  // Poll /billing/subscription until an active/trialing subscription appears.
  // Used only when we don't have a transaction id (e.g. hosted-checkout success
  // redirect without the id in the URL); in that case activation depends on
  // the Paddle webhook landing the subscription into our DB.
  const startSubscriptionPolling = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setActivating(true);
    setSuccess((prev) => prev ?? "Payment successful! Activating your subscription...");
    let elapsed = 0;

    const poll = () => {
      api
        .get<Subscription>("/billing/subscription")
        .then((sub) => {
          if (sub && (sub.status === "active" || sub.status === "trialing")) {
            finalizeActivation(sub);
          }
        })
        .catch(() => {});

      elapsed += ACTIVATE_INTERVAL;
      if (elapsed >= ACTIVATE_TIMEOUT) {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        setActivating(false);
        setSuccess(activationTimeoutMessage);
      }
    };

    setTimeout(poll, 1500);
    pollTimerRef.current = setInterval(poll, ACTIVATE_INTERVAL);
  };

  // Retry the server-side sync endpoint with a transaction id. This is the
  // primary path after a completed overlay checkout: the backend pulls the
  // subscription directly from Paddle, so activation does not depend on the
  // webhook firing. The first call may return `pending` if Paddle has not yet
  // created the subscription (it does so a few seconds after payment); we keep
  // retrying until it succeeds or we time out.
  const startSyncRetry = (transactionId: string) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setActivating(true);
    setSuccess((prev) => prev ?? "Payment successful! Activating your subscription...");
    let elapsed = 0;
    let inFlight = false;

    const attempt = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await api.post<{ status: string; subscription?: Subscription }>(
          "/billing/sync-transaction",
          { transaction_id: transactionId }
        );
        if (res.status === "synced" && res.subscription) {
          await finalizeActivation(res.subscription);
          return;
        }
        // `pending` — keep retrying.
      } catch {
        // Transient backend failure — keep retrying.
      } finally {
        inFlight = false;
      }

      elapsed += ACTIVATE_INTERVAL;
      if (elapsed >= ACTIVATE_TIMEOUT) {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        activeSyncTxnRef.current = null;
        clearPendingTxn();
        setActivating(false);
        setSuccess(activationTimeoutMessage);
      }
    };

    // First attempt runs soon; subsequent attempts on an interval.
    setTimeout(attempt, 500);
    pollTimerRef.current = setInterval(attempt, ACTIVATE_INTERVAL);
  };

  // Entry point used by every "checkout just finished" code path.
  // Dedupes against activeSyncTxnRef so the in-page (checkout.completed)
  // and post-redirect (?checkout=success with stashed txn id) paths don't
  // both fire sync retries at the same time.
  const activateAfterCheckout = (transactionId?: string) => {
    if (transactionId && activeSyncTxnRef.current === transactionId) return;
    setActivating(true);
    setSuccess("Payment successful! Activating your subscription...");
    if (transactionId) {
      activeSyncTxnRef.current = transactionId;
      stashPendingTxn(transactionId);
      startSyncRetry(transactionId);
    } else {
      // No transaction id known — wait on the webhook and poll.
      startSubscriptionPolling();
    }
  };

  // Handle checkout=success redirect from Paddle hosted / overlay checkout.
  // Paddle's success redirect navigates away from the page before any JS we
  // queued on checkout.completed gets to run. We stashed the transaction_id
  // in sessionStorage before opening checkout — pick it up here and drive
  // sync with it. If it's missing, fall back to polling on the webhook.
  useEffect(() => {
    if (searchParams.get("checkout") !== "success") return;
    const stashed = readPendingTxn();
    clearPendingTxn();
    clearCheckoutParams();
    activateAfterCheckout(stashed ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle _ptxn query parameter — Paddle payment link redirect flow.
  // Open checkout once then clear the param so page refresh doesn't reopen it.
  const ptxnHandledRef = useRef(false);
  useEffect(() => {
    const ptxn = searchParams.get("_ptxn");
    if (!ptxn || !paddle || ptxnHandledRef.current) return;
    ptxnHandledRef.current = true;
    stashPendingTxn(ptxn);
    paddle.Checkout.open({
      transactionId: ptxn,
      success: checkoutSuccessUrl,
      settings: { successUrl: checkoutSuccessUrl },
    });
    clearCheckoutParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, paddle]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // Listen for Paddle overlay events. On completion we force-close the overlay
  // (Paddle's own auto-dismiss is unreliable depending on dashboard settings)
  // and drive activation via the sync endpoint. This covers the case where
  // Paddle does NOT redirect (dashboard default-payment-link missing) — the
  // post-redirect handler covers the case where it does.
  useEffect(() => {
    if (!paddle) return;

    paddle.Update({
      eventCallback: (event: PaddleEvent) => {
        if (event.name === "checkout.completed") {
          const txnId = event.data?.transaction_id || event.data?.id;
          setTimeout(() => {
            try {
              paddle.Checkout.close();
            } catch {
              /* overlay may already be closing — ignore */
            }
          }, 50);
          activateAfterCheckout(txnId);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paddle]);

  const hasActiveSubscription =
    subscription !== null &&
    (subscription.status === "active" || subscription.status === "past_due" || subscription.status === "trialing");

  const isCancellationPending = Boolean(subscription?.cancel_at);

  // Effective plan: prefer subscription's plan_id (live from API) over tenant's plan_id (may be stale in JWT).
  // This ensures correct state immediately after checkout, before JWT refreshes.
  const effectivePlanId = hasActiveSubscription ? subscription!.plan_id : currentPlan;
  const currentPlanData = plans.find((p) => p.id === effectivePlanId);
  const isCurrentPlanAddonEnabled = currentPlanData?.addon_enabled ?? false;

  // Add-on section is shown when tenant is on an addon-enabled plan with an active subscription
  const showAddonSection = hasActiveSubscription && isCurrentPlanAddonEnabled;

  const addonQty = addon?.quantity ?? 0;
  const effectiveLimits = addon?.effective_limits ?? {
    max_checks: currentPlanData?.max_checks ?? 0,
    max_users: currentPlanData?.max_users ?? 0,
    max_alert_channels: currentPlanData?.max_alert_channels ?? 0,
  };

  // The free plan is the one with price=0
  const freePlan = plans.find((p) => p.price_monthly_cents === 0 && p.price_yearly_cents === 0);

  const formatPrice = (plan: Plan) => {
    if (plan.price_monthly_cents === 0 && plan.price_yearly_cents === 0) return "Free";
    if (billingCycle === "monthly") return `$${plan.price_monthly_cents / 100}/mo`;
    return `$${plan.price_yearly_cents / 100}/yr`;
  };

  const isPaidPlan = (plan: Plan) =>
    plan.price_monthly_cents > 0 || plan.price_yearly_cents > 0;

  const isDowngrade = (plan: Plan) => {
    if (!isPaidPlan(plan)) return true; // downgrading to free
    if (!currentPlanData) return false;
    return plan.price_monthly_cents < currentPlanData.price_monthly_cents;
  };

  const handleCheckout = async (planId: string) => {
    setLoading(planId);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ checkout_url: string; transaction_id: string }>(
        "/billing/checkout",
        { plan_id: planId, billing_cycle: billingCycle }
      );
      if (paddle && res.transaction_id) {
        stashPendingTxn(res.transaction_id);
        paddle.Checkout.open({
          transactionId: res.transaction_id,
          success: checkoutSuccessUrl,
          settings: { successUrl: checkoutSuccessUrl },
        });
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err) {
      setError(billingErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  // Auto-trigger checkout when ?plan= is present (e.g. from signup with ?plan=pro).
  // Waits for plans to load, then finds matching plan by name and starts checkout.
  const autoCheckoutTriggered = useRef(false);
  useEffect(() => {
    const planParam = searchParams.get("plan");
    if (!planParam || autoCheckoutTriggered.current || plans.length === 0 || hasActiveSubscription) return;
    const targetPlan = plans.find(
      (p) => p.name.toLowerCase() === planParam.toLowerCase() && (p.price_monthly_cents > 0 || p.price_yearly_cents > 0)
    );
    if (targetPlan) {
      autoCheckoutTriggered.current = true;
      handleCheckout(targetPlan.id);
    }
  }, [searchParams, plans, hasActiveSubscription]);

  const handleChangePlan = async (planId: string) => {
    setLoading(planId);
    setError(null);
    setConfirmDowngrade(null);
    setShowCancelDialog(false);
    setShowAddonDowngradeDialog(false);
    try {
      const res = await api.post<{ status: string; is_downgrade?: boolean; effective_at?: string }>(
        "/billing/change-plan",
        { plan_id: planId, billing_cycle: billingCycle }
      );
      const [sub, tenant, addonData] = await Promise.all([
        api.get<Subscription>("/billing/subscription").catch(() => null),
        api.get<{ plan_id: string }>("/tenants/current"),
        api.get<AddonInfo>("/billing/addon").catch(() => null),
      ]);
      setSubscription(sub);
      setCurrentPlan(tenant.plan_id);
      setAddon(addonData);

      if (res.is_downgrade && res.effective_at) {
        const targetPlan = plans.find((p) => p.id === planId);
        const planName = targetPlan?.name ?? planId;
        const date = formatDate(res.effective_at);
        setSuccess(
          `Your plan will change to ${planName} at the end of your current billing period on ${date}.`
        );
      } else {
        setSuccess("Plan upgraded successfully. Your new plan is now active.");
      }
    } catch (err) {
      setError(billingErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  const handlePlanClick = (plan: Plan) => {
    // Block all plan actions while polling for subscription activation
    if (activating) return;
    if (!hasActiveSubscription) {
      if (isPaidPlan(plan)) handleCheckout(plan.id);
      return;
    }
    if (!isPaidPlan(plan)) {
      // Downgrading to free
      if (addonQty > 0 && freePlan) {
        // Warn about addon loss
        setShowAddonDowngradeDialog(true);
      } else {
        setShowCancelDialog(true);
      }
      return;
    }
    if (isDowngrade(plan)) {
      setConfirmDowngrade(plan.id);
      return;
    }
    handleChangePlan(plan.id);
  };

  const handleManageSubscription = async () => {
    setLoading("manage");
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ portal_url: string }>("/billing/portal", {});
      if (res.portal_url) window.location.href = res.portal_url;
    } catch (err) {
      setError(billingErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ message: string; period_end: string }>("/billing/cancel", {});
      setShowCancelDialog(false);
      const endDate = res.period_end ? formatDate(res.period_end) : "";
      setSuccess(
        res.message ||
          (endDate
            ? `Your subscription will remain active until ${endDate}. After that, you'll be moved to the Free plan.`
            : "Your subscription will end at the current billing period.")
      );
      api
        .get<Subscription>("/billing/subscription")
        .then((r) => setSubscription(r))
        .catch(() => {});
    } catch (err) {
      setShowCancelDialog(false);
      setError(billingErrorMessage(err));
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePurchaseAddon = async () => {
    setLoading("addon-purchase");
    setError(null);
    setSuccess(null);
    try {
      // Backend creates a Paddle transaction for the addon price and returns
      // the transaction_id. It does NOT charge anything on its own — the
      // tenant has to complete payment via the Paddle overlay, identical to
      // the base-plan checkout flow.
      const res = await api.post<{ checkout_url: string; transaction_id: string }>(
        "/billing/addon",
        {}
      );
      if (paddle && res.transaction_id) {
        stashPendingTxn(res.transaction_id);
        paddle.Checkout.open({
          transactionId: res.transaction_id,
          success: checkoutSuccessUrl,
          settings: { successUrl: checkoutSuccessUrl },
        });
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setError("Billing is not fully configured — contact support.");
      }
    } catch (err) {
      setError(billingErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  const handleRemoveAddon = async () => {
    setLoading("addon-remove");
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ status: string; addon_quantity: number; effective_at?: string }>(
        "/billing/addon/remove",
        {}
      );
      const addonData = await api.get<AddonInfo>("/billing/addon").catch(() => null);
      setAddon(addonData);
      const msg = res.effective_at
        ? `Add-on package removal scheduled. Your limits will decrease on ${formatDate(res.effective_at)}.`
        : "Add-on package removed. Your limits have been updated.";
      setSuccess(msg);
    } catch (err) {
      setError(billingErrorMessage(err));
    } finally {
      setLoading(null);
    }
  };

  const getPlanButtonLabel = (plan: Plan) => {
    if (loading === plan.id) return "Loading...";
    if (activating && isPaidPlan(plan)) return "Activating...";
    if (!hasActiveSubscription && plan.trial_days > 0) return "Start Free Trial";
    if (!hasActiveSubscription) return "Upgrade";
    if (isDowngrade(plan)) return "Downgrade";
    return "Upgrade";
  };

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      {showCancelDialog && (
        <CancelConfirmDialog
          periodEnd={subscription?.status === "trialing" ? (subscription?.trial_end ?? "") : (subscription?.current_period_end ?? "")}
          isTrialing={subscription?.status === "trialing"}
          onConfirm={handleCancelConfirm}
          onCancel={() => setShowCancelDialog(false)}
          loading={cancelLoading}
        />
      )}
      {showAddonDowngradeDialog && freePlan && (
        <DowngradeWithAddonDialog
          addonQty={addonQty}
          freePlan={freePlan}
          onConfirm={() => {
            setShowAddonDowngradeDialog(false);
            setShowCancelDialog(true);
          }}
          onCancel={() => setShowAddonDowngradeDialog(false)}
          loading={false}
        />
      )}
      <div className="max-w-4xl mx-auto py-8 px-6">
        <h1 className="text-2xl font-bold mb-6">Billing</h1>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <span className="flex-1">{error}</span>
            <button
              className="shrink-0 opacity-70 hover:opacity-100"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              &times;
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-green-400/40 bg-green-400/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
            <span className="flex-1">{success}</span>
            <button
              className="shrink-0 opacity-70 hover:opacity-100"
              onClick={() => setSuccess(null)}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        {/* Active trial banner */}
        {subscription?.status === "trialing" && (() => {
          const daysLeft = trialDaysRemaining(subscription.trial_end);
          const isExpiring = daysLeft <= 3;
          return (
            <div className={`mb-6 rounded-lg border px-4 py-4 ${isExpiring ? "border-amber-400/60 bg-amber-50/80 dark:bg-amber-950/30" : "border-blue-400/40 bg-blue-50/80 dark:bg-blue-950/20"}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className={`font-semibold text-sm ${isExpiring ? "text-amber-800 dark:text-amber-200" : "text-blue-800 dark:text-blue-200"}`}>
                    {isExpiring
                      ? `Trial ending soon — ${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`
                      : `Free trial active — ${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`}
                  </p>
                  <p className={`text-xs mt-1 ${isExpiring ? "text-amber-700 dark:text-amber-300" : "text-blue-700 dark:text-blue-300"}`}>
                    {subscription.trial_end
                      ? `Your trial ends on ${formatDate(subscription.trial_end)}. Subscribe now to keep access.`
                      : "Subscribe now to keep access after your trial ends."}
                  </p>
                </div>
                <button
                  className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded transition-colors ${isExpiring ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                  disabled={loading === "subscribe-now"}
                  onClick={async () => {
                    setLoading("subscribe-now");
                    setError(null);
                    try {
                      const res = await api.post<{ portal_url: string }>("/billing/portal", {});
                      if (res.portal_url) window.location.href = res.portal_url;
                    } catch (err) {
                      setError(billingErrorMessage(err));
                    } finally {
                      setLoading(null);
                    }
                  }}
                >
                  Subscribe now
                </button>
              </div>
            </div>
          );
        })()}

        {confirmDowngrade && (
          <div className="mb-6 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-4 text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
              Confirm plan downgrade
            </p>
            <p className="text-amber-700 dark:text-amber-300 mb-3">
              Your plan will change to{" "}
              <strong>{plans.find((p) => p.id === confirmDowngrade)?.name ?? confirmDowngrade}</strong>{" "}
              at the end of your current billing period
              {subscription?.current_period_end
                ? <> on <strong>{formatDate(subscription.current_period_end)}</strong></>
                : ""}
              . You keep full access until then.
            </p>
            <div className="flex gap-3">
              <button
                className="px-4 py-2 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-50"
                disabled={loading === confirmDowngrade}
                onClick={() => handleChangePlan(confirmDowngrade)}
              >
                {loading === confirmDowngrade ? "Loading..." : "Confirm Downgrade"}
              </button>
              <button
                className="px-4 py-2 bg-muted text-foreground rounded text-sm hover:opacity-80"
                onClick={() => setConfirmDowngrade(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-lg border bg-card p-1">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                billingCycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setBillingCycle("monthly")}
            >Monthly</button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                billingCycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setBillingCycle("yearly")}
            >Yearly</button>
          </div>
        </div>

        {/* Plan cards — only active plans, sorted by price, Free first */}
        <div className={`grid grid-cols-1 gap-4 ${plans.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
          {plans.map((plan) => {
            const isCurrent = plan.id === effectivePlanId;
            const isFreePlan = plan.price_monthly_cents === 0 && plan.price_yearly_cents === 0;
            const isTrialing = isCurrent && subscription?.status === "trialing";

            return (
              <div
                key={plan.id}
                className={`bg-card rounded-lg shadow p-6 ${isCurrent ? "ring-2 ring-primary" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-semibold">{plan.name}</h2>
                  {isTrialing && subscription?.trial_end && (
                    <span className="shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                      Trial — ends {formatDate(subscription.trial_end)}
                    </span>
                  )}
                  {plan.trial_days > 0 && !hasActiveSubscription && !activating && (
                    <span className="shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                      {plan.trial_days}-day free trial
                    </span>
                  )}
                </div>
                <p className="text-3xl font-bold mt-2">{formatPrice(plan)}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li>
                    {isCurrent && showAddonSection && !isFreePlan
                      ? formatLimit(effectiveLimits.max_checks)
                      : formatLimit(plan.max_checks)}{" "}
                    checks
                  </li>
                  <li>
                    {isCurrent && showAddonSection && !isFreePlan
                      ? formatLimit(effectiveLimits.max_users)
                      : formatLimit(plan.max_users)}{" "}
                    users
                  </li>
                  <li>
                    {isCurrent && showAddonSection && !isFreePlan
                      ? formatLimit(effectiveLimits.max_alert_channels)
                      : formatLimit(plan.max_alert_channels)}{" "}
                    alert channels
                  </li>
                  {plan.addon_enabled && (
                    <li className="text-primary text-xs font-medium">
                      Expandable with add-on packages
                    </li>
                  )}
                </ul>
                {/* Activating state: show spinner on paid plan card during post-checkout polling */}
                {activating && isPaidPlan(plan) && !isCurrent ? (
                  <div className="mt-4">
                    <p className="text-sm text-primary font-medium animate-pulse">
                      Activating your subscription...
                    </p>
                  </div>
                ) : isCurrent ? (
                  <div className="mt-4">
                    <p className="text-sm text-primary font-medium">
                      Current plan
                    </p>
                    {hasActiveSubscription && (
                      isCancellationPending ? (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 font-medium">
                          Cancelling at end of period
                          {subscription?.cancel_at ? <> ({formatDate(subscription.cancel_at)})</> : ""}
                        </p>
                      ) : (
                        <button
                          className="mt-2 text-xs text-muted-foreground hover:text-destructive underline transition-colors"
                          onClick={() => setShowCancelDialog(true)}
                        >
                          {subscription?.status === "trialing" ? "Cancel Trial" : "Cancel Subscription"}
                        </button>
                      )
                    )}
                  </div>
                ) : isFreePlan ? (
                  // Free plan button — only show for users with active subscriptions who want to downgrade
                  hasActiveSubscription && !isCancellationPending ? (
                    <button
                      className="mt-4 w-full bg-muted text-foreground rounded py-2 text-sm hover:opacity-80 disabled:opacity-50"
                      onClick={() => handlePlanClick(plan)}
                    >
                      Downgrade to Free
                    </button>
                  ) : isCancellationPending ? (
                    <p className="mt-4 text-xs text-muted-foreground">Pending downgrade</p>
                  ) : (
                    <p className="mt-4 text-sm text-muted-foreground">Free plan</p>
                  )
                ) : (
                  <button
                    className="mt-4 w-full bg-primary text-primary-foreground rounded py-2 text-sm hover:opacity-90 disabled:opacity-50"
                    disabled={loading === plan.id || activating}
                    onClick={() => handlePlanClick(plan)}
                  >
                    {getPlanButtonLabel(plan)}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Post-trial expired state: subscription exists with cancelled status and had a trial */}
        {subscription?.status === "cancelled" && subscription.trial_start && currentPlanData && currentPlanData.price_monthly_cents === 0 && (
          <div className="mt-4 rounded-lg border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">Your trial has ended</p>
            <p className="text-xs mt-1 text-amber-700 dark:text-amber-300">
              Upgrade to a paid plan to restore access to premium features.
            </p>
          </div>
        )}

        {/* Add-on packages section — only for Pro (addon_enabled) subscribers */}
        {showAddonSection && currentPlanData && (
          <div className="mt-8 bg-card rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-1">Add-on Packages</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Each package adds the same resource quota as your base plan.
            </p>

            {/* Limits summary */}
            <div className="rounded-md bg-muted/50 px-4 py-3 text-sm mb-4">
              <span className="text-muted-foreground">
                Base {currentPlanData.name}:{" "}
                <strong>{formatLimit(currentPlanData.max_checks)} checks</strong>,{" "}
                <strong>{formatLimit(currentPlanData.max_users)} users</strong>
              </span>
              {addonQty > 0 && (
                <>
                  <span className="mx-2 text-muted-foreground">|</span>
                  <span className="text-muted-foreground">
                    +{addonQty} package{addonQty !== 1 ? "s" : ""}:{" "}
                    <strong className="text-foreground">
                      {formatLimit(effectiveLimits.max_checks)} checks,{" "}
                      {formatLimit(effectiveLimits.max_users)} users total
                    </strong>
                  </span>
                </>
              )}
            </div>

            {/* Persistent pending-cancel indicator. Mirrors the base plan's
                "Cancelling at end of period" line so the state is visible
                across reloads, not only in the flash banner right after the
                Remove click. */}
            {addon?.cancel_at && addonQty > 0 && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">
                Removal scheduled — your add-on ends on <strong>{formatDate(addon.cancel_at)}</strong>.
              </p>
            )}

            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-sm">
                <span className="text-muted-foreground">Current packages: </span>
                <span className="font-semibold">{addonQty}</span>
              </div>
              <button
                className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-colors"
                disabled={loading === "addon-purchase"}
                onClick={handlePurchaseAddon}
              >
                {loading === "addon-purchase" ? "Processing..." : "Add Package (+$10/mo)"}
              </button>
              <button
                className="px-4 py-2 bg-muted text-foreground rounded text-sm font-medium hover:opacity-80 disabled:opacity-50 transition-colors"
                disabled={addonQty === 0 || loading === "addon-remove" || Boolean(addon?.cancel_at)}
                onClick={handleRemoveAddon}
                title={addon?.cancel_at ? "Removal already scheduled" : undefined}
              >
                {loading === "addon-remove" ? "Processing..." : "Remove Package"}
              </button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Changes are prorated on your current billing cycle.
            </p>
          </div>
        )}

        {hasActiveSubscription && (
          <div className="mt-8 pt-6 border-t">
            <button
              className="text-sm text-muted-foreground hover:text-foreground underline disabled:opacity-50"
              disabled={loading === "manage"}
              onClick={handleManageSubscription}
            >
              {loading === "manage" ? "Loading..." : "Manage subscription in billing portal"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
